import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { getformAnalytics } from "api/services/automation/dashboard-tan";
import { useQuery } from "react-query";
import { ResType } from "types";
import { StyledTaskBox } from "views/dashboard/OrgDashboard/styles";
import { useNavigate } from "react-router-dom";
import { Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, BarChart } from "recharts";
import Loader from "components/Loader";
// import { BarChart } from '@mui/x-charts';
import { useState } from "react";
import { getFormsFinancialYears } from "utils/getFinancialYears";
import { getCurrentFinancialYear, getCurrentQuarter } from "pages/atom-pro/Tan/constants";

function TanFormsAnalytics() {
  const navigate = useNavigate();
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear(new Date()));

  const handleFinancialYearChange = (e) => {
    setFinancialYear(e.target.value);
  };

  const { data, isLoading }: ResType = useQuery(
    ["tan-tds-form-analytics", financialYear],
    getformAnalytics
  );

  const translations = {
    form24Q: "Form 24Q Filed",
    form26Q: "Form 26Q Filed",
    form27Q: "Form 27Q Filed",
    form27EQ: "Form 27EQ Filed",
    form24QNotFiled: "Form 24Q Not Filed",
    form26QNotFiled: "Form 26Q Not Filed",
    form27QNotFiled: "Form 27Q Not Filed",
    form27EQNotFiled: "Form 27EQ Not Filed",
  } as const;

  const analyticsData = [
    {
      type: "Q1",
      form24Q: data?.data?.["Form 24Q"]?.byQuarter?.Q1?.filed || 0,
      form24QNotFiled: data?.data?.["Form 24Q"]?.byQuarter?.Q1?.notFiled || 0,
      form26Q: data?.data?.["Form 26Q"]?.byQuarter?.Q1?.filed || 0,
      form26QNotFiled: data?.data?.["Form 26Q"]?.byQuarter?.Q1?.notFiled || 0,
      form27Q: data?.data?.["Form 27Q"]?.byQuarter?.Q1?.filed || 0,
      form27QNotFiled: data?.data?.["Form 27Q"]?.byQuarter?.Q1?.notFiled || 0,
      form27EQ: data?.data?.["Form 27EQ"]?.byQuarter?.Q1?.filed || 0,
      form27EQNotFiled: data?.data?.["Form 27EQ"]?.byQuarter?.Q1?.notFiled || 0,
    },
    {
      type: "Q2",
      form24Q: data?.data?.["Form 24Q"]?.byQuarter?.Q2?.filed || 0,
      form24QNotFiled: data?.data?.["Form 24Q"]?.byQuarter?.Q2?.notFiled || 0,
      form26Q: data?.data?.["Form 26Q"]?.byQuarter?.Q2?.filed || 0,
      form26QNotFiled: data?.data?.["Form 26Q"]?.byQuarter?.Q2?.notFiled || 0,
      form27Q: data?.data?.["Form 27Q"]?.byQuarter?.Q2?.filed || 0,
      form27QNotFiled: data?.data?.["Form 27Q"]?.byQuarter?.Q2?.notFiled || 0,
      form27EQ: data?.data?.["Form 27EQ"]?.byQuarter?.Q2?.filed || 0,
      form27EQNotFiled: data?.data?.["Form 27EQ"]?.byQuarter?.Q2?.notFiled || 0,
    },
    {
      type: "Q3",
      form24Q: data?.data?.["Form 24Q"]?.byQuarter?.Q3?.filed || 0,
      form24QNotFiled: data?.data?.["Form 24Q"]?.byQuarter?.Q3?.notFiled || 0,
      form26Q: data?.data?.["Form 26Q"]?.byQuarter?.Q3?.filed || 0,
      form26QNotFiled: data?.data?.["Form 26Q"]?.byQuarter?.Q3?.notFiled || 0,
      form27Q: data?.data?.["Form 27Q"]?.byQuarter?.Q3?.filed || 0,
      form27QNotFiled: data?.data?.["Form 27Q"]?.byQuarter?.Q3?.notFiled || 0,
      form27EQ: data?.data?.["Form 27EQ"]?.byQuarter?.Q3?.filed || 0,
      form27EQNotFiled: data?.data?.["Form 27EQ"]?.byQuarter?.Q3?.notFiled || 0,
    },
    {
      type: "Q4",
      form24Q: data?.data?.["Form 24Q"]?.byQuarter?.Q4?.filed || 0,
      form24QNotFiled: data?.data?.["Form 24Q"]?.byQuarter?.Q4?.notFiled || 0,
      form26Q: data?.data?.["Form 26Q"]?.byQuarter?.Q4?.filed || 0,
      form26QNotFiled: data?.data?.["Form 26Q"]?.byQuarter?.Q4?.notFiled || 0,
      form27Q: data?.data?.["Form 27Q"]?.byQuarter?.Q4?.filed || 0,
      form27QNotFiled: data?.data?.["Form 27Q"]?.byQuarter?.Q4?.notFiled || 0,
      form27EQ: data?.data?.["Form 27EQ"]?.byQuarter?.Q4?.filed || 0,
      form27EQNotFiled: data?.data?.["Form 27EQ"]?.byQuarter?.Q4?.notFiled || 0,
    },
  ];

  let highestCount = 0;

  for (const form in data) {
    const formData = data[form];
    const totalCount = formData.filed + formData.notFiled;

    if (totalCount > highestCount) {
      highestCount = totalCount;
    }
  }

  const filterAnalyticsData = (data) => {
    const currentDate = new Date();
    const currentQuarter = getCurrentQuarter(currentDate);
    if (currentQuarter === "Q1") {
      return [];
    }
    const currentQuarterIndex = ["Q1", "Q2", "Q3", "Q4"].indexOf(currentQuarter);
    return data.filter((item) => {
      const itemQuarterIndex = ["Q1", "Q2", "Q3", "Q4"].indexOf(item.type);
      return itemQuarterIndex < currentQuarterIndex;
    });
  };

  function addLabels<T extends { dataKey: keyof typeof translations }>(series: T[]) {
    return series.map((item) => ({
      ...item,
      label: translations[item.dataKey],
      valueFormatter: (v: number | null) => (v ? `${v}` : "0"),
    }));
  }

  const handleClick = (params, type, filingStatus) => {
    navigate("/atom-pro/income-tax-tan/tds-forms", {
      state: {
        financialQuarter: params?.type,
        financialYear: financialYear,
        filingStatus,
        formCd: type,
      },
    });
  };
  const formatTooltip = (value: any, name: string) => {
    return [`${value}`, translations[name as keyof typeof translations] || name];
  };

  const finalData =
    new Date().getFullYear() == financialYear ? filterAnalyticsData(analyticsData) : analyticsData;

  if (isLoading) return <Loader />;

  return (
    <Box>
      <StyledTaskBox>
        <header style={{ backgroundColor: "#e6f2ff" }}>
          <Box display="flex" gap={1} width={"95%"}>
            <Typography variant="h6">TDS Regular Form Analytics</Typography>
          </Box>
          <Box>
            <TextField
              name="financialYear"
              value={financialYear}
              onChange={handleFinancialYearChange}
              size="small"
              label="Financial Year"
              select
              sx={{ width: "250px" }}
            >
              {getFormsFinancialYears().map((year: string) => (
                <MenuItem key={year} value={year.split("-")[0]}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </header>

        <main>
          <ResponsiveContainer width="95%" height={350}>
            <BarChart data={finalData}>
              <XAxis dataKey="type" fontSize={12} style={{ fontWeight: "bold" }} />
              <YAxis
                domain={[0, highestCount + 1]}
                tickFormatter={(value) => Math.round(value).toString()}
                fontSize={11}
                style={{ fontWeight: "bold" }}
              />
              <Tooltip
                formatter={formatTooltip}
                labelStyle={{ fontWeight: "bold", fontSize: 14 }}
                itemStyle={{ fontSize: 12, fontWeight: "bold" }}
                cursor={{ fill: "transparent" }}
              />
              <Bar
                dataKey="form24Q"
                stackId="a"
                fill="#8565C5"
                barSize={15}
                cursor="pointer"
                onClick={(v) => handleClick(v, "Form 24Q", "Filed")}
              ></Bar>
              <Bar
                dataKey="form24QNotFiled"
                stackId="a"
                fill="#9E6060"
                barSize={15}
                cursor="pointer"
                onClick={(v) => handleClick(v, "Form 24Q", "Not Filed")}
              ></Bar>
              <Bar
                dataKey="form26Q"
                stackId="b"
                fill="#E9BD35"
                barSize={15}
                cursor="pointer"
                onClick={(v) => handleClick(v, "Form 26Q", "Filed")}
              ></Bar>
              <Bar
                dataKey="form26QNotFiled"
                stackId="b"
                fill="#4DBEEC"
                barSize={15}
                cursor="pointer"
                onClick={(v) => handleClick(v, "Form 26Q", "Not Filed")}
              ></Bar>
              <Bar
                dataKey="form27Q"
                stackId="c"
                fill="#FD4641"
                barSize={15}
                cursor="pointer"
                onClick={(v) => handleClick(v, "Form 27Q", "Filed")}
              ></Bar>
              <Bar
                dataKey="form27QNotFiled"
                stackId="c"
                fill="#51C474"
                barSize={15}
                cursor="pointer"
                onClick={(v) => handleClick(v, "Form 27Q", "Not Filed")}
              ></Bar>
              <Bar
                dataKey="form27EQ"
                stackId="d"
                fill="#8C7368"
                barSize={15}
                cursor="pointer"
                onClick={(v) => handleClick(v, "Form 27EQ", "Filed")}
              ></Bar>
              <Bar
                dataKey="form27EQNotFiled"
                stackId="d"
                fill="#5c3c92"
                barSize={15}
                cursor="pointer"
                onClick={(v) => handleClick(v, "Form 27EQ", "Not Filed")}
              ></Bar>
            </BarChart>
          </ResponsiveContainer>
        </main>
      </StyledTaskBox>
    </Box>
  );
}

export default TanFormsAnalytics;
